/**
 * "You were signed out because this account was used on another device."
 *
 * The concurrent-login guard on the server evicts the OLDER of two sessions in
 * the same slot by answering 401 with `code: "SESSION_SUPERSEDED"` - on every
 * authenticated request (middleware) and on token refresh (serializer, where
 * it arrives as `detail: "SESSION_SUPERSEDED: ..."`).
 *
 * Until this existed the web client had nothing listening for that answer.
 * The evicted browser simply started failing with 401s, whichever page it was
 * on turned that into a generic "Session expired" and wiped storage, and the
 * login page's `?signedOut=other-device` explanation - which has been there
 * all along - was never shown because nothing ever set the flag.
 *
 * Registered once, at app bootstrap (see App.tsx), on the global axios
 * instance every request in this app goes through.
 *
 * DELIBERATELY DOES NOT CALL THE SIGNOUT ENDPOINT. The slot now belongs to the
 * device that displaced us; asking the server to release it would be asking to
 * sign out the device the user is actually using. `clear_session` refuses that
 * anyway (session id mismatch), so the call would be a wasted round trip.
 * The mobile app takes the same line.
 */

import axios from 'axios';

import { clearTokens } from '@/services/tokenService';
import { CONTEXT_SEEN_KEY } from '@/services/tenantClaims';

export const EVICTION_QUERY = 'signedOut=other-device';

const LOGIN_PATH = '/login';

/** True for the 401 the session guard sends when this session was displaced. */
export const isSessionSuperseded = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 401) return false;

  const body = (error.response.data ?? {}) as Record<string, unknown>;
  if (body.code === 'SESSION_SUPERSEDED') return true;
  return String(body.detail ?? body.error ?? '').includes('SESSION_SUPERSEDED');
};

let handled = false;

/**
 * Forget this browser's session WITHOUT telling the server. Shared with
 * services/tenantHostAccess.ts, which ends a session for a different reason
 * but must leave exactly the same nothing behind.
 */
export const clearLocalSession = (): void => {
  try {
    clearTokens();
    for (const key of ['user', 'isAuthenticated', 'accessData', 'userEmail', 'session_id', CONTEXT_SEEN_KEY]) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable - the caller's redirect still ends the session.
  }
};

/**
 * Drop the local session and land on the login page with the explanation.
 * Idempotent: a page that fires several requests at once gets several 401s,
 * and only the first may act.
 */
export const handleSessionEviction = (): void => {
  if (handled) return;
  handled = true;

  clearLocalSession();

  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(LOGIN_PATH)) {
    handled = false; // already there; let the next eviction act again
    return;
  }
  window.location.replace(`${LOGIN_PATH}?${EVICTION_QUERY}`);
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSessionSuperseded(error)) handleSessionEviction();
    return Promise.reject(error);
  },
);
